import { Injectable } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpResponse, HttpSentEvent, HttpHeaderResponse, HttpProgressEvent, HttpUserEvent } from "@angular/common/http";
import { Observable, BehaviorSubject, throwError } from "rxjs";
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig } from "@angular/material/legacy-dialog";
import { filter, catchError, take, switchMap, finalize, tap } from "rxjs/operators";

import { AuthenticationService, LoaderService, AlertService } from "../_services";
import { IUser } from "../_models";
import { AppGlobals } from "../app.globals";
import { ConfirmationDialogComponent } from "../_components/confirmationDialog/confirmationDialog.component";

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    constructor(public authenticationService: AuthenticationService, private loaderService: LoaderService, private alertService: AlertService, private appGlobals: AppGlobals, private dialog: MatDialog) {

    }

    isRefreshingToken = false;
    tokenSubject = new BehaviorSubject<string>(null);
    pendingRequests = 0;

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any> | any> {
        return next.handle(this.addTokenToRequest(request))
            .pipe(
                (tap((event: HttpEvent<any>) => {
                    if (event instanceof HttpResponse) {
                        if (event.body && event.body.success)
                            this.alertService.showMessage(event.body.success.message, event.body.success.title, this.appGlobals.alertMessageTypes.success);

                        //this.onEnd();
                    }
                })) as any, 
                catchError((error) => {
                    //log error 
                    //this.onEnd();

                    if (error instanceof HttpErrorResponse) {
                        switch ((error).status) {
                            default:
                                return this.handleError(error);
                            case 401:
                                return this.handle401Error(request, next);
                            case 409:
                                return this.handle409Error(error);
                        }
                    } else {
                        return throwError(error);
                    }
                }),
                finalize(() => {
                    this.onEnd();
                }));
    }

    private addTokenToRequest(request: HttpRequest<any>): HttpRequest<any> {
        this.showLoader();

        if (!request.url.includes("token")) {
            //add authorization header with jwt token if available
            const currentUser = this.authenticationService.currentUserValue;

            if (currentUser && currentUser.accessToken) {
                request = request.clone({
                    setHeaders: {
                        Authorization: `Bearer ${currentUser.accessToken}`
                    }
                });
            }
        }
        return request;
    }

    private handleError(error) {
        if (error && ((error.status === 400 && error.error && error.error.error === "invalid_grant") || (error.status === 401))) {
            // If logged in and we get a 400 and the error message is 'invalid_grant', the token is no longer valid so logout.
            this.logoutUser();

            if (error.error.error_description) {
                let additionalText = "";

                if (error.error.error_description.indexOf("password is incorrect") !== -1) {
                    const failedLoginAttempts = error.error.error_description.split("/")[1];
                    const allowedStudentFailedLogins = error.error.error_description.split("/")[2];

                    if (allowedStudentFailedLogins > 0 && failedLoginAttempts > 2 && failedLoginAttempts <= allowedStudentFailedLogins - 1)
                        additionalText = ` You have ${failedLoginAttempts} failed login attempts. Consider resetting your password before your account gets inactivated.`;

                    error.error.error_description = error.error.error_description.split("/")[0];
                }

                this.alertService.showMessage(`${error.error.error_description}${additionalText}`, "An Error Has Occurred", this.appGlobals.alertMessageTypes.error);
            }
        } else {
            try {
                this.alertService.showMessage(error.error.error_description ? error.error.error_description : error.statusText ? error.statusText : error.message, "An Error Has Occurred", this.appGlobals.alertMessageTypes.error);
            } catch (e) {
                this.alertService.showMessage("An error occurred", "", this.appGlobals.alertMessageTypes.error);
            }
        }

        return throwError(error);
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;

            // Reset here so that the following requests wait until the token
            // comes back from the refreshToken call.
            this.tokenSubject.next(null);

            return this.authenticationService.refreshAccessToken()
                .pipe(
                    switchMap((user: IUser) => {
                        if (user) {
                            this.tokenSubject.next(user.accessToken);

                            return next.handle(this.addTokenToRequest(request));
                        } else
                            return this.logoutUser();
                    }),
                    catchError(() => {
                        return this.logoutUser();
                    }),
                    finalize(() => {
                        this.isRefreshingToken = false;
                        this.onEnd(true);
                    })
                );
        } else {
            return this.tokenSubject
                .pipe(filter((token: any) => token != null),
                    take(1),
                    switchMap(() => {
                        this.isRefreshingToken = false;
                        this.onEnd(false);

                        return next.handle(this.addTokenToRequest(request));
                    }));
        }
    }
    
    private handle409Error(error: any) {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.closeOnNavigation = true;
        dialogConfig.width = "25%";
        dialogConfig.height = "auto";
        dialogConfig.panelClass = "confirmation-dialog-container";
        dialogConfig.data =
        {
            title: "Data Updated Error",
            content: `The data has been updated and needs to be reloaded before you can proceed.`
        };

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(result => {
            if (result === "confirm") {
                location.reload();
            }
        });

        return throwError(error);
    }

    private logoutUser(): Observable<any> {
        this.onEnd(true);

        if (this.authenticationService.currentUserValue)
            this.authenticationService.logout(true);

        return new Observable<any>();
    }

    private onEnd(isRefreshTokenEnd?: boolean): void {
        if (isRefreshTokenEnd)
            this.pendingRequests = 0;
        else
            this.pendingRequests--;

        //console.log("end " + this.pendingRequests + isRefreshTokenEnd);

        if (this.pendingRequests <= 0) {
            //console.log("hiding loader");
            this.hideLoader();
        }
    }

    private showLoader(): void {
        this.pendingRequests++;

        //console.log("show " + this.pendingRequests);

        this.loaderService.show();
    }

    private hideLoader(): void {
        if (this.pendingRequests < 0)
            this.pendingRequests = 0;

        this.loaderService.hide();
    }
}